/* Sprint 2R-2 — Driver app "Min performance" page.
 *
 * Driver-only. Only shows the logged-in driver's own performance data.
 * No customer revenue / Stripe / Billy / EMA margin fields.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ArrowLeft, CheckCircle2, AlertTriangle, TrendingUp,
  TrendingDown, Minus, Package, Clock, XCircle, Star, Lightbulb,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function driverApi() {
  const token = localStorage.getItem("ema_driver_token");
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const STATUS = {
  good: { cls: "bg-green-100 text-green-800 border-green-200", label: "God", Icon: CheckCircle2 },
  watchlist: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Observation", Icon: AlertTriangle },
  high_risk: { cls: "bg-red-100 text-red-800 border-red-200", label: "Høj risiko", Icon: AlertTriangle },
  no_data: { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Ingen data", Icon: Clock },
};

const TREND = {
  improving: { cls: "text-green-700", Icon: TrendingUp, label: "Forbedring" },
  declining: { cls: "text-red-700", Icon: TrendingDown, label: "Forværring" },
  stable: { cls: "text-brand-text2", Icon: Minus, label: "Stabil" },
  no_data: { cls: "text-brand-text2", Icon: Minus, label: "Ingen data" },
};

export default function DriverMyPerformance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await driverApi().get("/driver/me/performance?days=30");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        window.location.href = "/driver/login";
        return;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center" data-testid="driver-perf-loading">
        <Loader2 className="animate-spin text-brand-red" size={32}/>
      </div>
    );
  }
  if (!data) return null;

  const st = STATUS[data.status_v2] || STATUS.no_data;
  const tr = TREND[data.trend] || TREND.no_data;
  const StIcon = st.Icon;
  const TrIcon = tr.Icon;

  return (
    <div className="min-h-screen bg-brand-bg pb-12" data-testid="driver-my-performance">
      <div className="bg-brand-dark text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button onClick={() => navigate("/driver/my-routes")} className="text-white/90"
                  data-testid="perf-back" aria-label="Tilbage">
          <ArrowLeft size={22}/>
        </button>
        <div className="font-display font-bold text-base">Min performance</div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status + score */}
        <div className="bg-white border-2 border-brand-red p-4">
          <div className="flex items-center justify-between mb-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${st.cls}`} data-testid="perf-status">
              <StIcon size={12}/> {st.label}
            </span>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${tr.cls}`} data-testid="perf-trend">
              <TrIcon size={14}/> {tr.label}
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-bold">Samlet score</div>
          <div className="font-display font-bold text-4xl text-brand-dark mt-1" data-testid="perf-score">
            {data.score_v2 != null ? `${data.score_v2}/100` : "—"}
          </div>
          <div className="text-xs text-brand-text2 mt-1">Sidste 30 dage · Metode {data.method_version}</div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Kpi label="Gennemførte ruter" value={data.completed_routes ?? 0} testid="kpi-routes"/>
          <Kpi label="Leverede pakker" value={data.completed_packages ?? 0} testid="kpi-packages"/>
          <Kpi label="On-time levering" value={`${Math.round((data.on_time_rate ?? 0) * 100)} %`} testid="kpi-ontime"/>
          <Kpi label="On-time pickup" value={`${Math.round((data.on_time_pickup_rate ?? 0) * 100)} %`} testid="kpi-ontime-pickup"/>
          <Kpi label="Fejlleveringer" value={data.failed_deliveries ?? 0} testid="kpi-fail"/>
          <Kpi label="Sent ankomst" value={data.late_starts ?? 0} testid="kpi-late"/>
        </div>

        {/* Penalty signals */}
        <div className="bg-white border border-brand p-4" data-testid="perf-signals">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2">Hændelser de sidste 30 dage</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Signal label="No-show" value={data.no_show_count} Icon={XCircle} bad={data.no_show_count > 0}/>
            <Signal label="Scan-uoverensstemmelser" value={data.scan_mismatch_count} Icon={Package} bad={data.scan_mismatch_count > 0}/>
            <Signal label="Klager" value={data.complaints_count} Icon={AlertTriangle} bad={data.complaints_count > 0}/>
            <Signal label="Advarsler" value={data.warnings_count} Icon={AlertTriangle} bad={data.warnings_count > 0}/>
          </div>
        </div>

        {/* Customer rating */}
        {data.customer_rating != null && (
          <div className="bg-white border border-brand p-4 flex items-center justify-between" data-testid="perf-rating">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold">Kundebedømmelse</div>
              <div className="font-display font-bold text-xl text-brand-dark">{data.customer_rating}/5</div>
            </div>
            <Star className="text-yellow-500" size={28}/>
          </div>
        )}

        {/* Improvement tips */}
        <div className="bg-white border border-brand p-4" data-testid="perf-tips">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2 flex items-center gap-1">
            <Lightbulb size={14}/> Sådan kan du forbedre din score
          </div>
          <ul className="space-y-2">
            {(data.improvement_tips || []).map((tip, i) => (
              <li key={i} className="text-sm bg-brand-bg2 border-l-2 border-brand-red px-3 py-2">
                {tip}
              </li>
            ))}
          </ul>
        </div>

        <div className="text-[11px] text-brand-text2 italic px-1 pt-2">
          Scoreberegningen er gennemsigtig: 100 - 8 × no-show - 4 × scan-mismatch - 5 × klager - 6 × advarsler, justeret efter on-time- og fejlleveringsrate.
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-white border border-brand p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-semibold">{label}</div>
      <div className="font-display font-bold text-lg text-brand-dark mt-0.5">{value}</div>
    </div>
  );
}

function Signal({ label, value, Icon, bad }) {
  return (
    <div className={`flex items-center gap-2 p-2 border ${bad ? "border-red-300 bg-red-50" : "border-brand bg-brand-bg2"}`}>
      <Icon size={16} className={bad ? "text-red-700" : "text-brand-text2"}/>
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider text-brand-text2 font-semibold">{label}</div>
        <div className={`font-bold ${bad ? "text-red-700" : "text-brand-dark"}`}>{value ?? 0}</div>
      </div>
    </div>
  );
}
